/**
 * Collation of tools used for MandatoryContent to encapsulate logic in the DS
 * Keywords: ManCon, MandatoryContent, DueDates, Due Date Rules
 **/
import {getDueDateInDays} from '../constants/dueDateConfig';
import type {UnitType} from '../constants/dueDateConfig';

export type TeamWithDueDate = {
  dueDateRuleValue?: number;
  dueDateDisplayUnit?: string;
};

/**
 * Determines if a team is mandatory based on having a due date set
 * @param team - Team object with potential due date properties
 * @returns true if team has a due date value greater than 0
 */
export function teamIsMandatory(team: TeamWithDueDate) {
  return teamHasDueDateSet(team);
}

/**
 * Identify mandatory content teams - teams that have content assigned with a due date
 */
function teamHasDueDateSet(team: TeamWithDueDate) {
  return (team.dueDateRuleValue ?? 0) > 0;
}

/**
 * Filters out teams that have due dates set
 * Useful for excluding mandatory teams from certain operations
 *
 * @param teams - Array of teams with due date properties
 * @returns Array containing only teams without due dates
 *
 * Note: Everyone Team doesn't (and shouldn't have a due date set) - entries are populated in the content_assignment table to assign due dates to the content and team
 * Currently this goes against the usual, expected, behavior of the everyone team, where it swaps content_assignment. This function can be used whilst the table has dual function
 * in order to return to the prev state
 */
export function filterOutTeamsWithDueDates<T>(teams: (T & TeamWithDueDate)[]) {
  return teams.filter((team) => !teamHasDueDateSet(team));
}

/**
 * Filter to have ONLY teams with a due date
 *
 * @param teams - Array of teams with due date properties
 * @returns Array containing only teams with due dates set
 * @example const mandatoryTeams = filterToOnlyTeamsWithDueDates(allTeams);
 */
export function filterToOnlyTeamsWithDueDates<T>(
  teams: (T & TeamWithDueDate)[]
) {
  return teams.filter(teamHasDueDateSet);
}

/**
 * Util function to enhance {teamsArray} with dueDate data from {referenceArray} returning dueDateRuleValue and dueDateDisplayUnit
 *
 * @param teamsArray - Base array of teams to enhance
 * @param referenceArray - Reference array containing due date data
 * @returns Enhanced teams array with due date properties merged from reference
 * @example
 * const enhancedTeams = wrapTeamWithDueDateData(teams, mandatoryTeams);
 * // Teams now have dueDateRuleValue and dueDateDisplayUnit properties
 */
export function wrapTeamWithDueDateData<
  T1 extends HasId,
  T2 extends HasId & TeamWithDueDate,
>(teamsArray: T1[], referenceArray: T2[]) {
  return mergeArraysById(teamsArray, referenceArray, [
    'dueDateRuleValue',
    'dueDateDisplayUnit',
  ]);
}

type HasId = {
  id: number;
};

/**
 * Util function used to combine arrays based on criterion
 * will add prop(s) from reference array to base array items by matching ID
 *
 * @param base - Base array to enhance with additional properties
 * @param reference - Reference array containing properties to merge
 * @param props - Property key(s) to merge from reference to base items
 * @returns Array with base items enhanced with properties from reference array where IDs match
 */
export function mergeArraysById<
  T1 extends HasId,
  T2 extends HasId,
  K extends keyof T2,
>(base: T1[], reference: T2[], props: K | K[]): ((T1 & Pick<T2, K>) | T1)[] {
  const getReferenceItemById = findArrayMatchingIdByMap<T2>(reference);
  const propsToMerge = Array.isArray(props) ? props : [props];

  return base.map((baseItem) => {
    const referenceItem = getReferenceItemById(baseItem.id);

    if (!referenceItem) {
      return {...baseItem};
    }

    const mergedProps = propsToMerge.reduce(
      (acc, prop) => {
        acc[prop] = referenceItem[prop];
        return acc;
      },
      {} as Pick<T2, K>
    );

    return {
      ...baseItem,
      ...mergedProps,
    };
  });
}

function findArrayMatchingIdByMap<T extends HasId>(reference: T[]) {
  const lookupMap = new Map(reference.map((item) => [item.id, item]));
  return function (referenceId: number): T | undefined {
    return lookupMap.get(referenceId);
  };
}

function getTeamDueDateInDays(team: TeamWithDueDate): number | null {
  const {dueDateDisplayUnit, dueDateRuleValue} = team;
  if (!dueDateDisplayUnit || !dueDateRuleValue) {
    return null;
  }
  return getDueDateInDays(dueDateRuleValue, dueDateDisplayUnit as UnitType);
}

export function dueDateRulesAreDifferent(
  baseRule: TeamWithDueDate,
  compareRule: TeamWithDueDate
): boolean {
  // Using == null is idiomatically correct for checking both null and undefined
  // eslint-disable-next-line eqeqeq
  if (baseRule == null && compareRule == null) {
    return false;
  }

  if (!baseRule || !compareRule) {
    return true;
  }

  const baseRuleAsDays = getTeamDueDateInDays(baseRule);
  const compareRuleAsDays = getTeamDueDateInDays(compareRule);

  return baseRuleAsDays !== compareRuleAsDays;
}

export function hasMandatoryTeams(teams?: TeamWithDueDate[]): boolean {
  return !!teams?.some(teamIsMandatory);
}
